import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { validateContact } from "../../lib/validation";
import { createContactMessage } from "../../lib/api";
import { SUPPORT_EMAIL } from "../../config/links";

const EMPTY = { full_name: "", email: "", phone: "", message: "" };

const Field = ({ id, label, error, as: Tag = "input", ...input }) => (
  <div>
    <label htmlFor={`contact-${id}`} className="mb-1.5 block text-[13px] font-semibold text-[#2a2214]">{label}</label>
    <Tag id={`contact-${id}`} className="field" aria-invalid={!!error} aria-describedby={error ? `contact-${id}-error` : undefined} data-testid={`contact-${id}-input`} {...input} />
    {error && <p id={`contact-${id}-error`} className="mt-1.5 text-[13px] text-[#b3261e]" data-testid={`contact-${id}-error`}>{error}</p>}
  </div>
);

const Success = () => (
  <div className="text-center sm:text-left" data-testid="contact-success">
    <h2 className="font-display text-5xl uppercase leading-none text-[#120e07]">Message sent</h2>
    <p className="mt-4 text-[15px] leading-relaxed text-[#4a3f2a]">Thanks for reaching out. Our team will reply to your email soon.</p>
    <Link to="/" className="mt-6 inline-block text-[14px] font-medium text-[#6b5320] underline decoration-[#e8b84a] underline-offset-4 hover:text-[#17130c]" data-testid="contact-back-home-link">← Back to home</Link>
  </div>
);

export const ContactForm = () => {
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => {
    setValues((s) => ({ ...s, [k]: e.target.value }));
    if (errors[k]) setErrors((s) => ({ ...s, [k]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validateContact(values);
    setErrors(errs);
    setApiError("");
    if (Object.keys(errs).length) return;
    setSubmitting(true);
    try {
      await createContactMessage({
        full_name: values.full_name.trim(),
        email: values.email.trim(),
        phone: values.phone.trim() || null,
        message: values.message.trim(),
      });
      setSent(true);
    } catch {
      setApiError(`Something went wrong sending your message. Please try again or email ${SUPPORT_EMAIL}.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pearl-card fade-up rounded-2xl px-5 py-7 text-[#17130c] [animation-delay:120ms] sm:px-9 sm:py-10" data-testid="contact-card">
      {sent ? <Success /> : (
        <form onSubmit={submit} noValidate className="space-y-4" data-testid="contact-form">
          <Field id="full_name" label="Full name" autoComplete="name" value={values.full_name} onChange={set("full_name")} error={errors.full_name} />
          <Field id="email" label="Email" type="email" inputMode="email" autoComplete="email" value={values.email} onChange={set("email")} error={errors.email} />
          <Field id="phone" label="Mobile (optional)" type="tel" inputMode="tel" autoComplete="tel-national" value={values.phone} onChange={set("phone")} error={errors.phone} />
          <Field id="message" as="textarea" rows={6} maxLength={2000} label="How can we help?" value={values.message} onChange={set("message")} error={errors.message} className="field min-h-[140px] resize-y" />
          {apiError && <p role="alert" className="rounded-lg border border-[#b3261e]/30 bg-[#b3261e]/10 px-3 py-2.5 text-[13px] text-[#8c1d18]" data-testid="contact-api-error">{apiError}</p>}
          <button type="submit" disabled={submitting} className="flex w-full items-center justify-center gap-2 rounded-full border border-[#e8b84a]/60 bg-[#0a0a0a] px-6 py-4 text-[15px] font-bold tracking-[0.08em] text-[#f3d27c] transition-[transform,background-color] duration-200 hover:-translate-y-px hover:bg-black disabled:opacity-70" data-testid="contact-submit-button">
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            SEND MESSAGE →
          </button>
          <p className="text-center text-[12px] text-[#6b5d40]">We'll only use your details to reply to your message.</p>
        </form>
      )}
    </div>
  );
};
